import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import qs from 'qs'

import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { saveOnboardingV2Progress, initializeOnboardingV2Progress, getOnboardingV2Progress } from '../../util/onboarding-v2-storage'

import { OnboardingV2Data } from './index'
import { StepConfig } from '../../util/hooks/use-multi-step-form'

import { ICountry } from '@vacationtracker/shared/data/countries'
import { ILeavePolicy } from '../../types/locations'

// Define the shape of our context state
export interface OnboardingContextState {
  currentStepIndex: number
  formData: OnboardingV2Data
  isLastStep: boolean
  isLoading: boolean
  errors: Record<string, string>
  userId?: string
  companyId?: string
  steps?: StepConfig<OnboardingV2Data>[]
}

// Define action types for our reducer
export type OnboardingAction =
  | { type: 'UPDATE_FORM_DATA'; payload: Partial<OnboardingV2Data> }
  | { type: 'UPDATE_LOCATION_COUNTRY'; payload: ICountry | undefined }
  | { type: 'UPDATE_LEAVE_POLICIES'; payload: ILeavePolicy[] | undefined }
  | { type: 'GO_TO_NEXT_STEP' }
  | { type: 'GO_TO_PREVIOUS_STEP' }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERRORS'; payload: Record<string, string> }
  | { type: 'RESET_ONBOARDING' }
  | { type: 'SET_USER_CONTEXT'; payload: { userId: string; companyId: string; steps: StepConfig<OnboardingV2Data>[] } }
  | { type: 'MARK_STEP_COMPLETED'; payload: { stepId: string; hasCompletedOnboarding?: boolean } }

// Create the context
export const OnboardingContext = createContext<OnboardingContextState | undefined>(undefined)
export const OnboardingDispatchContext = createContext<React.Dispatch<OnboardingAction> | undefined>(undefined)

// Reducer function
export const onboardingReducer = (state: OnboardingContextState, action: OnboardingAction): OnboardingContextState => {
  switch (action.type) {
    case 'UPDATE_FORM_DATA':
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
      }

    case 'UPDATE_LOCATION_COUNTRY':
      return {
        ...state,
        formData: { ...state.formData, country: action.payload },
      }

    case 'UPDATE_LEAVE_POLICIES':
      return {
        ...state,
        formData: { ...state.formData, leavePolicies: action.payload },
      }

    case 'GO_TO_NEXT_STEP': {
      const nextStepIndex = state.currentStepIndex + 1
      return {
        ...state,
        currentStepIndex: nextStepIndex,
      }
    }

    case 'GO_TO_PREVIOUS_STEP':
      return {
        ...state,
        currentStepIndex: Math.max(0, state.currentStepIndex - 1),
      }

    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStepIndex: action.payload,
      }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }

    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.payload,
      }

    case 'SET_USER_CONTEXT':
      return {
        ...state,
        userId: action.payload.userId,
        companyId: action.payload.companyId,
        steps: action.payload.steps,
      }

    case 'MARK_STEP_COMPLETED':
      // Only save progress if user has onboardingV2 URL parameter
      // This check is done in the component that calls this action
      if (state.userId && state.companyId && state.steps) {
        saveOnboardingV2Progress(state.userId, state.companyId, action.payload.stepId, state.currentStepIndex, state.steps.length, action.payload.hasCompletedOnboarding)
      }
      return state

    case 'RESET_ONBOARDING':
      return {
        currentStepIndex: 0,
        formData: {
          hasSeenWelcome: false,
          locationName: '',
          locationTimezone: '',
          country: undefined,
          leavePolicies: undefined,
          departmentName: '',
          leaveType: '',
          leaveStartDate: '',
          leaveEndDate: '',
          leaveReason: '',
          hasCompletedOnboarding: false,
        },
        isLastStep: false,
        isLoading: false,
        errors: {},
        userId: state.userId,
        companyId: state.companyId,
        steps: state.steps,
      }

    default:
      return state
  }
}

// Provider component
interface OnboardingProviderProps {
  children: ReactNode
  initialData: OnboardingV2Data
  steps: StepConfig<OnboardingV2Data>[]
  userId?: string
  companyId?: string
}

const getInitialState = (steps: StepConfig<OnboardingV2Data>[], initialData: OnboardingV2Data): OnboardingContextState => ({
  currentStepIndex: 0,
  formData: initialData,
  isLastStep: steps.length <= 1,
  isLoading: false,
  errors: {},
})

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children, initialData, steps, userId, companyId }) => {
  const location = useLocation()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [state, dispatch] = useReducer(onboardingReducer, getInitialState(steps, initialData))

  // Set user context when userId and companyId are available
  React.useEffect(() => {
    if (userId && companyId) {
      // Parse URL parameters to check if onboardingV2 is enabled
      const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
      const hasOnboardingV2Param = queryParams.onboardingV2 === 'true'

      // Only initialize progress if user has the onboardingV2 URL parameter
      if (hasOnboardingV2Param) {
        initializeOnboardingV2Progress(
          userId,
          companyId,
          3 // Only count the 3 main setup steps (Location, Department, Leave Request)
        )
      }

      dispatch({
        type: 'SET_USER_CONTEXT',
        payload: { userId, companyId, steps },
      })

      // Navigate to the first incomplete relevant step only if onboarding already started
      const relevantOrderedStepIds = ['location-setup', 'department-setup', 'leave-request']
      const progress = getOnboardingV2Progress(userId, companyId)
      const completed = progress?.completedSteps || []
      const progressStarted = !!progress && (completed.length > 0 || (progress.currentStepIndex ?? 0) > 0 || !!progress.hasCompletedOnboarding)
      if (progressStarted) {
        const nextStepId = relevantOrderedStepIds.find((id) => !completed.includes(id)) || 'completion'
        const targetIndex = steps.findIndex((s) => s.id === nextStepId)
        if (targetIndex >= 0) {
          dispatch({ type: 'SET_CURRENT_STEP', payload: targetIndex })
        }
      }
    }
  }, [userId, companyId, steps, location.search, authCompany?.plan])

  return (
    <OnboardingContext.Provider value={state}>
      <OnboardingDispatchContext.Provider value={dispatch}>{children}</OnboardingDispatchContext.Provider>
    </OnboardingContext.Provider>
  )
}

// Custom hook to use the onboarding context
export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext)
  const dispatch = useContext(OnboardingDispatchContext)

  if (context === undefined || dispatch === undefined) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider')
  }

  return { ...context, dispatch }
}

// Hook to mark a step as completed
export const useMarkStepCompleted = () => {
  const dispatch = useContext(OnboardingDispatchContext)

  return useCallback(
    (stepId: string, hasCompletedOnboarding = false) => {
      if (dispatch) {
        dispatch({
          type: 'MARK_STEP_COMPLETED',
          payload: { stepId, hasCompletedOnboarding },
        })
      }
    },
    [dispatch]
  )
}
